import { useEffect, useState } from "react";
import Button from "../Button";
import LabeledInput from "../LabeledInput";
import PropTypes from "prop-types";
import { getFullAccountByAccountId } from "../../api/api";
import dayjs from 'dayjs';
import 'dayjs/locale/en';
import { useCurrencyFormatter } from "../../hooks/useCurrencyFormatter";
import ConditionStatementButton from "../Modals/ConditionStatementButton";
import { useTranslateProduct } from '../../hooks/useTranslateProduct';
import { useTranslation } from "react-i18next";

function PrimaryDetails({ accountID, handleModalClose }) {
  const [primaryDetails, setPrimaryDetails] = useState({});
  const translateProduct = useTranslateProduct();
  const { t } = useTranslation();
  const formatCurrency = useCurrencyFormatter();
  
  useEffect(() => {
    const fetchData = async () => {
      try {
        const apiData = await getFullAccountByAccountId(accountID);
        if (apiData) {
          setPrimaryDetails(apiData);
        } else {
          console.error('Failed to fetch data for Deal Details.');
        }
      } catch (error) {
        console.error('Error fetching data for Deal Details:', error.message);
      }
    };

    fetchData();
  }, [accountID]);

  const formatDate = (dateString) => {
    const date = dayjs(dateString);
    return date.isValid() ? date.format('YYYY-MM-DD') : 'N/A';
  };

  return (
    <div className="border-2 p-5">
      <div className="border-2 p-2 flex justify-between items-center rounded-lg mb-5">
        <span className="labels font-semibold mr-2">
          {primaryDetails.mortgageAccountNumber ?? t(`nullDate`)} - {primaryDetails.primaryBorrowerFirstName || t(`nullDate`)} {primaryDetails.primaryBorrowerLastName || t(`nullDate`)} - 
          {primaryDetails.outstandingConditions > 0 ? (
            <span className="text-red-500 ml-1">
              ({t('dealView.primaryDetails.outCon')} {primaryDetails.outstandingConditions})
            </span>
          ) : (
            <span className="ml-1"> 
              ({t('dealView.primaryDetails.outCon')} {primaryDetails.outstandingConditions || 0})
            </span>
          )}
        </span>

        <div className="flex justify-around gap-2">
          <div className="flex flex-row gap-2">
            <div /* data-tooltip-id="uploadTooltip" data-tooltip-content="Upload Documents" */>
              <Button accountID={accountID} variant={"docUpload"} handleModalClose={handleModalClose} />
            </div>
            <ConditionStatementButton accountID={accountID} />
          </div>
        </div>
      </div>

      <div className="flex flex-col md:flex-row justify-between items-start gap-5">
        <div className="grid grid-cols-1 md:grid-cols-3 w-full gap-6">
          {/* First row */}
          <LabeledInput 
            label={"Amount"} 
            type="string" 
            value={formatCurrency(primaryDetails.totalMortgageAmount || 0)} 
          />
          <LabeledInput 
            label={"Interest"} 
            type="string" 
            value={primaryDetails.interestRate ? `${primaryDetails.interestRate}%` : t(`nullDate`)} 
          />
          <LabeledInput 
            label={"Closing"} 
            type="string" 
            value={formatDate(primaryDetails.closingDate)} 
          />

          {/* Second row */}
          <LabeledInput 
            label={"Property"} 
            type="string" 
            value={primaryDetails.securedAddress || t(`nullDate`)} 
            widthClass="col-span-1 md:col-span-2"
            labelWidth="w-1/2 md:w-1/4"
          />
          <LabeledInput 
            label={"Product"} 
            type="string" 
            value={translateProduct(primaryDetails.product) || t(`nullDate`)} 
          />
        </div>
      </div>

      {/* <Tooltip id="uploadTooltip" place="left" effect="solid" /> */}
    </div>
  );
}

PrimaryDetails.propTypes = {
  accountID: PropTypes.string.isRequired,
  handleModalClose: PropTypes.func.isRequired,
};

export default PrimaryDetails;